import React from "react";
import { Component } from "react";
import PropTypes from "prop-types";
import { isMobile, isTablet, isIOS, isAndroid } from "react-device-detect";
import { has, get, includes, remove, clone } from "lodash";
import { currentAnswer } from "../state/questionStates.js";

import C from "../constants";
import Form from "./forms/Form";
import Question from "./question/Question";
import Summary from "./Summary";
import Complete from "./Complete";
import CameraQuestion from "./cameraQuestion/CameraQuestion";
import BarcodeScanner from "./barcodeScanner/BarcodeScanner";
import * as qStates from "../state/questionStates";
import ExternalCall from "./actions/ExternalCall";
import * as fieldValidation from "../state/fieldValidation";
import { findAnswer } from '../state/questionStates';
import QuoteResults from "./actions/attestiv/QuoteResults";
import PseudoQuoteHandler from "./actions/attestiv/PseudoQuoteHandler";
import PhoneExaminationResults from "./actions/attestiv/PhoneExaminationResults";
import FacialExaminationResults from "./actions/attestiv/FacialExaminationResults";

import strings from "../resources/strings";
import { getAnalysisTypes, getArgusAnalysis } from "../helpers/analysis";

import appCfg from "../config";
import attestivNet from "../lib/AttestivNet";
import CodeWordGenerator from "./actions/attestiv/CodeWordGenerator";
import CodeWordValidator from "./actions/attestiv/CodeWordValidator";

const isDesktopDevice = !isIOS && !isAndroid && !isMobile && !isTablet;
const fingerprintModule = require("../lib/fingerprint");

function getAnalysisModels(question) {
    const analysisModels = getAnalysisTypes(clone(get(question, 'analysis', [])));
    return includes(analysisModels, "argus") ? ["argus"] : analysisModels;
}

function shouldUseArgus(question) {
    return Boolean(getArgusAnalysis(get(question, 'analysis', [])));
}

// Load the WASM connector module
var attestivFingerprint = undefined;
fingerprintModule().then(async instance => {
    console.debug("Loaded fingerprint module " + await instance.get_version());
    attestivFingerprint = instance;
});

class QuestionContainer extends Component {
    async processDeviceInput(question, store, file, option, isFullSizeImage, advanceToNextQuestion) {
        store.dispatch({ type: C.STORE_MEDIA_FILE_DATA, file: file, question: question, isFullSizeImage: isFullSizeImage });
        store.dispatch({ type: C.INCREMENT_FILE_UPLOAD_COUNTER });

        const analysisConfig = appCfg.imageAnalysis;

        // Gather any media processing to be done and let it finish down below
        let processMediaFuncs = [], backgroundMediaFuncs = [];

        if (isDesktopDevice) {
            store.dispatch({ type: C.APPEND_MEDIA_FILE_DATA, uuid: file.uuid, data: { tags: ["import"] }});
        }
        
        if (option.tags) {
            store.dispatch({ type: C.APPEND_MEDIA_FILE_DATA, uuid: file.uuid, data: { tags: option.tags }});
        }
        
        let fingerprint = await this.getFingerprintFromFile(file);
        // Run any analysis if specified
        let analysisModels = getAnalysisModels(question);
        let ranCombinedDetection = false;

        // triageFull and objectDetection will be run on the server
        if (includes(analysisModels, "triageFull")) {
            remove(analysisModels, (element) => {
                return element === "triageFull";
            });
        }
        if (includes(analysisModels, "objectDetection")) {
            remove(analysisModels, (element) => {
                return element === "objectDetection";
            });
        }

        for (let model of analysisModels) {
            if (!has(analysisConfig, model)) {
                console.warn(strings.getString("noAnalysisModel", model));
                continue;
            }

            let modelConfig = analysisConfig[model];
            if (modelConfig.serverSide) {
                continue;
            }
            modelConfig.maxRetryRequests = get(question, "maxRetryRequests", 0);

            // We may need to pass extra body args to this model
            // Example:
            // "analysis" : ["debugPopCollection"],
            // "analysisArgs": {"label": "pic"},
            // Note this will send the same body to every model!
            // I hacked this together for my pop collection workflow,
            // need to redesign this before using it for real
            let questionArgs = get(question, 'analysisArgs', {});

            // only run the models on jpeg files
            if (file.isJPEG || (file.type === 'application/pdf' && model === "decpageExtraction")) {
                // check if questionArgs has 'findAnswer' definition and replace with value
                const findAnswerDef = get(questionArgs, 'findAnswer');
                if (findAnswerDef) {
                    // replace questionArgs with answer from question's field value
                    questionArgs = {};
                    const value = get(findAnswer(store.getState(), findAnswerDef.questionID, findAnswerDef.fieldID), 'value');
                    if (value) {
                        questionArgs[findAnswerDef.argName] = value;
                    }
                }

                // if triage and object detection are both in the analysisModels list, then send a single /detect call so that
                // the image is only uploaded once to the forensic server
                let promise;
                if ((model === "triageFull" && includes(analysisModels, "objectDetection")) 
                    || (model === "objectDetection" && includes(analysisModels, "triageFull")))
                {
                    if (ranCombinedDetection) {
                        continue;
                    }
                    ranCombinedDetection = true;
                    const customMetricsTags = this.getCustomMetricsTags(store, fingerprint);
                    promise = attestivNet.executeModel(store, file, analysisConfig["detect"], question, questionArgs,
                        store.getState().currentAnswerIndex, modelConfig.runInBackground, customMetricsTags);
                }
                else {
                    promise = attestivNet.executeModel(store, file, modelConfig, question, questionArgs,
                        store.getState().currentAnswerIndex, modelConfig.runInBackground);
                }

                // if this is a model that runs in the background, then add it to the list of models in the question to be executed
                // in the background when we advance to the next question.
                if (modelConfig.runInBackground) {
                    backgroundMediaFuncs.push(promise);
                }
                else {
                    processMediaFuncs.push(promise);
                }
            }
        }

        // only weber will have analysis config set by questionSpecifier
        // if this is present, run a document scan
        // We'd like to remove question specifiers and push this into the 'analysis' block above
        let weberConfig = appCfg.imageAnalysis[question.questionSpecifier];
        if (weberConfig && !shouldUseArgus(question)) {
            let func = attestivNet.executeDocumentScan(weberConfig, file, question, store);
            processMediaFuncs.push(func)
        }
    
        let state = store.getState();
        if (backgroundMediaFuncs.length > 0) {
            store.dispatch({type: C.ADD_BACKGROUND_MEDIA_FUNCS, backgroundMediaFuncs: backgroundMediaFuncs, answerIndex: state.currentAnswerIndex});
            Promise.all(backgroundMediaFuncs).then(() => {
                console.debug("*** UPLOADING ANSWER DATA - BACKGROUND FUNCS DONE");
                store.dispatch({type: C.UPLOAD_ANSWER_DATA, questionID: question.questionID, answerIndex: state.currentAnswerIndex});
                store.dispatch({type: C.REMOVE_BACKGROUND_MEDIA_FUNCS, answerIndex: state.currentAnswerIndex});
            });
        }

        let result = Promise.all(processMediaFuncs).then(function() {
            console.log("Completed all foreground processing");
        });

        // if there are no background tasks to run like when uploading a pdf or arbitrary non-image file, then this logic will start the upload
        // of those files
        if (backgroundMediaFuncs.length === 0) {
            // we want to use store.getState() here to get the latest state to check scalingComplete flag, but want to use the state above from
            // when the background funcs were started to get the currentAnswerIndex when dispatching
            console.debug("*** UPLOADING ANSWER DATA - NO BACKGROUND FUNCS TO CALL");
            store.dispatch({type: C.UPLOAD_ANSWER_DATA, questionID: question.questionID, answerIndex: state.currentAnswerIndex});
        }

        return result;
    }

    getFingerprintFromFile = async function(file) {
        return new Promise(async (resolve, reject) => {
            let fingerprint;
            if (file.isJPEG) {  
                let fr = new FileReader();
                fr.onload = async function(evt) {
                    fingerprint = await attestivFingerprint.fingerprint_jpg(evt.target.result)
                    resolve(fingerprint);
                    console.log(fingerprint);
                }
                fr.readAsArrayBuffer(file);
            } else {
                fingerprint = await attestivFingerprint.fingerprint_file(file);
                resolve(fingerprint);
                console.log(fingerprint);
            }
        });
    }

    getCustomMetricsTags = function(store, fingerprint) {
        const state = store.getState();
        const answer = currentAnswer(state);
        const tags = get(answer, "tags", []);
        const recordId = get(state, "recordId");
        let customMetricsTags = [];
        if (tags){
            customMetricsTags.push("capture:" + includes(tags, "import") ? "import" : "attestivCapture");
        }
        if (recordId) {
            customMetricsTags.push("record_id:" + recordId);
        }
        if (fingerprint){
            customMetricsTags.push("fingerprint:" + fingerprint);
        }
        return customMetricsTags;
    }

    render() {
        let store = this.context.store;

        if (!store.getState().referenceId) {
            // invalid referenceId specified in URL
            return (
                <div className="w3-card w3-padding-small w3-animate-opacity">
                    Invalid identifier specified. Unable to proceed.
                </div>
            );
        }

        let current = qStates.currentQuestion(store.getState());
        if (!current) {
            return <div className="w3-card w3-padding-small w3-animate-opacity"></div>;
        }

        if (current.questionType === "form") {
            let shouldValidateField = (field) => {
                return field.fieldType === "text" || field.fieldType === "number" || field.fieldType === "email"
                        || field.fieldType === "date" || field.fieldType === "tel";
            };
            let changeFunction = (field, value) => {
                let fieldMatches = current.fields.filter(f => f.fieldID === field) || [];
                let currentField = fieldMatches.length > 0 ? fieldMatches[0] : null;
                if (currentField && currentField.fieldType === "date") {
                    let dateValue = new Date(Date.parse(value));
                    value = (dateValue.getMonth() + 1) + "-" + (dateValue.getDate() + 1) + "-" + dateValue.getFullYear();
                }

                store.dispatch({ type: C.ASSIGN_VALUE, field: field, value: value });

                if (currentField && shouldValidateField(currentField) && fieldValidation.needsValidation(currentField))
                {
                    let valid = fieldValidation.validateField(currentField, value);
                    let el = document.getElementById(currentField.fieldID + "Error");
                    if (el) {
                        el.innerHTML = valid ? "" : strings.getString("provideValidValue");
                        store.dispatch({ type: C.VALUE_FORMAT_ERROR, fieldID: currentField.fieldID, valueFormatError: !valid });
                    }
                }
            };

            let currentAnswer = qStates.currentAnswer(store.getState());
            return (
                <div className="w3-card w3-padding-small w3-animate-opacity">
                    <Form
                        question={current}
                        store={store}
                        answers={currentAnswer && currentAnswer.fields ? currentAnswer.fields : undefined}
                        onSave={changeFunction}
                    />
                </div>
            );
        } else if (current.questionType === "question") {
            return (
                <div className="w3-card w3-padding-small w3-animate-opacity">
                    <Question
                        question={current}
                        store={store}
                        selectionMade={option => {
                            store.dispatch({ type: option.action, selectedOption: option });
                        }}
                    />
                </div>
            );
        } else if (current.questionType === "info") {
            // same logic as a question, but allows you to put in a statement with maybe a Continue button
            return (
                <div className="w3-card w3-padding-small w3-animate-opacity">
                    <Question
                        question={current}
                        store={store}
                        selectionMade={option => {
                            store.dispatch({ type: option.action, selectedOption: option });
                        }}
                    />
                </div>
            );
        } else if (current.questionType === "camera" || current.questionType === "videoCamera"
                    || current.questionType === "allCameras" || current.questionType === "audioRecorder"
                    || current.questionType === "anyInput") {
            return (
                <CameraQuestion
                    question={current}
                    store={store}
                    processDeviceInput={(file, option, executeImageAnalysis, advanceToNextQuestion) => {
                        return this.processDeviceInput(current, store, file, option, executeImageAnalysis, advanceToNextQuestion);
                    }}
                    processClick={(action, state, nextQuestion) => {
                        store.dispatch({ type: action, state: state, nextQuestion: nextQuestion });
                    }}
                />
            );
        } else if (current.questionType === "barcodeScanner") {
            return (
                <BarcodeScanner
                    question={current}
                    store={store}
                    processPicture={(file, option) => {
                        this.processDeviceInput(current, store, file, option);
                    }}
                    processClick={(action, state, nextQuestion) => {
                        store.dispatch({ type: action, state: state, nextQuestion: nextQuestion });
                    }}
                />
            );
        } else if (current.questionType === "action") {
            if (current.questionSpecifier === "quote-results") {
                return <QuoteResults question={current} store={store} />
            } else if (current.questionSpecifier === "pseudo-quote-handler") {
                return <PseudoQuoteHandler question={current} store={store} />
            } else if (current.questionSpecifier === "phone-examination-results") {
                return <PhoneExaminationResults question={current} store={store} />
            } else if (current.questionSpecifier === "facial-examination-results") {
                return <FacialExaminationResults question={current} store={store} />
            } else if (current.questionSpecifier === "code-word-generator") {
                return <CodeWordGenerator question={current} store={store}
                    selectionMade={option => {
                        store.dispatch({ type: option.action, selectedOption: option });
                    }}
                />
            } else if (current.questionSpecifier === "code-word-validator") {
                return <CodeWordValidator question={current} store={store}
                    selectionMade={option => {
                        store.dispatch({ type: option.action, selectedOption: option });
                    }}
                />
            } else if (current.questionSpecifier === "external-call") {
                // TODO: Use the actionData.handler field to load the proper handler
                // Add error handling for when the component doesn't exist
                // For now, we only have pop testing (for us), and the homesite poc
                let path = "./actions/" + store.getState().organization;
                let handler, autoAdvance = true;
                switch (current.actionData.handler) {
                    case "poppernautUploader":
                        handler = require(path + "/PoppernautUploader");
                        break;
                    case "phoneExamination":
                        handler = require(path + "/PhoneExamination");
                        autoAdvance = false;
                        break;
                    case "generateAndEmailReport":
                        handler = require(path + "/ReportGenerator");
                        break;
                    default:
                        handler = require(path + "/GenerateQuoteHandler");
                        break;
                }

                return (<ExternalCall
                        question={current}
                        store={store}
                        autoAdvance={autoAdvance}
                        queryStringFunction={handler.constructQueryString}
                        payloadFunction={handler.constructPayload}
                        resultFunction={handler.handleResult}
                        errorFunction={handler.handleError}
                    />);
            }
        } else if (current.questionType === "submit") {
            return (
                <div>
                    <Summary store={store} />
                </div>
            );
        } else if (current.questionType === "complete") {
            if (get(store.getState(), "questionSet.clearLocalStorageOnCompletion", false)) {
                console.log("Clearing localStorage");
                localStorage.clear();
            }
            
            return (
                <div>
                    <Complete store={store} />
                </div>
            );
        } else {
            console.warn(strings.getString("unknownQuestionType", current.questionType, current.questionID));
            return (
                <div>
                    {strings.getString("unknownQuestionType", current.questionType, current.questionID)}
                </div>
            );
        }
    }
}

QuestionContainer.contextTypes = {
    store: PropTypes.object
};

export default QuestionContainer;
